'use client';

import { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { VolumeX, Volume2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslations } from 'next-intl';
import { ttsCache } from '@/utils/tts-cache';

interface TextToSpeechButtonProps {
  text: string;
  language?: string;
  voice?: string;
  disabled?: boolean;
}

export function TextToSpeechButton(props: TextToSpeechButtonProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const t = useTranslations('tts');

  const handleTogglePlayback = async () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    if (!props.text?.trim()) {
      console.warn('No text to speak');
      return;
    }

    try {
      setIsLoading(true);

      // Create cache request object
      const cacheRequest = {
        text: props.text,
        language: props.language || 'en',
        voice: props.voice || 'nova'
      };

      // Check cache first
      let audioBlob = ttsCache.get(cacheRequest);

      if (!audioBlob) {
        const response = await fetch('/api/speak', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text: props.text,
            language: props.language || 'en',
            voice: props.voice || 'nova'
          })
        });

        if (!response.ok) {
          throw new Error('Text to speech failed');
        }

        // Get the audio blob and cache it
        audioBlob = await response.blob();
        ttsCache.set(cacheRequest, audioBlob);
      }

      // Create a blob URL from the audio blob
      const audioUrl = URL.createObjectURL(audioBlob);

      // Create and play the audio
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        audioRef.current = null;
      };

      audio.onpause = () => {
        setIsPlaying(false);
      };

      audio.play();
      setIsPlaying(true);
    } catch (err) {
      console.error('Text to speech error:', err);
      toast.error("Text to speech failed! Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={handleTogglePlayback}
            disabled={props.disabled || isLoading || !props.text?.trim()}
            className={isPlaying ? "bg-blue-500 hover:bg-blue-600" : ""}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isPlaying ? (
              <VolumeX className="h-4 w-4" />
            ) : (
              <Volume2 className="h-4 w-4" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('buttonTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}